import React, { useMemo, useState, createElement } from 'react';
import { ExternalLink, Lock, X, Check, Plus, ToggleRight, ToggleLeft, ChevronRight, ChevronUp, ChevronDown, Trash2, Globe, ShieldCheck, AlertCircle, Clock, CheckCircle, FileCheck, GraduationCap, FileText, Upload, Save, Lightbulb, AlertTriangle, Search, Camera, Eye, Monitor, Smartphone, Star, MessageSquare, Calendar, Video, Mic, BarChart3, Pin, EyeOff, ArrowRight, Sparkles, MapPin, ShoppingBag } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useAstrologerProfile } from '../../contexts/AstrologerProfileContext';
interface ProfileTabProps {
  showToast: (msg: string) => void;
  onNavigate: (page: string) => void;
  setActiveTab?: (tab: string) => void;
}
interface ProfileBlock {
  id: string;
  label: string;
  enabled: boolean;
  location: string;
}
const ALL_SPECIALIZATIONS = ['Западная астрология', 'Натальная карта', 'Прогностика', 'Синастрия', 'Ведическая астрология', 'Хорарная астрология', 'Элективная астрология', 'Кармическая астрология', 'Медицинская астрология', 'Финансовая астрология', 'Детская астрология', 'Астропсихология', 'Нумерология', 'Таро', 'Астрокартография', 'Лунная астрология', 'Бизнес-астрология'];
const RECOMMENDED_ORDER = ['about', 'services', 'reviews', 'products', 'education', 'faq'];
const MAX_ABOUT_LENGTH = 1000;
export function ProfileTab({
  showToast,
  onNavigate,
  setActiveTab
}: ProfileTabProps) {
  const {
    profile,
    updateProfile
  } = useAstrologerProfile();
  // Preview mode
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [highlightedBlock, setHighlightedBlock] = useState<string | null>(null);
  // Main info — initialized from context
  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [experience, setExperience] = useState(profile.experience);
  const [languages, setLanguages] = useState(profile.languages);
  const [aboutText, setAboutText] = useState(profile.aboutText);
  const [showAboutHint, setShowAboutHint] = useState(false);
  // Specializations
  const [specializations, setSpecializations] = useState(profile.specializations);
  const [specSearch, setSpecSearch] = useState('');
  const [showSpecDropdown, setShowSpecDropdown] = useState(false);
  const [customSpecInput, setCustomSpecInput] = useState('');
  const [showCustomSpecInput, setShowCustomSpecInput] = useState(false);
  // Avatar
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile.avatarUrl);
  const handleAvatarUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          showToast('⚠️ Файл слишком большой (макс. 5 МБ)');
          return;
        }
        const url = URL.createObjectURL(file);
        setAvatarUrl(url);
        showToast('Фото профиля обновлено ✓');
      }
    };
    input.click();
  };
  // Contacts — initialized from context
  const [contacts, setContacts] = useState(profile.contacts);
  const [showChatButton, setShowChatButton] = useState(profile.showChatButton);
  const [showBookButton, setShowBookButton] = useState(profile.showBookButton);
  // Consultation settings — initialized from context
  const [consultPrice, setConsultPrice] = useState(profile.consultPrice);
  const [consultPriceType, setConsultPriceType] = useState<'fixed' | 'from'>(profile.consultPriceType);
  const [consultDuration, setConsultDuration] = useState(profile.consultDuration);
  const [consultFormats, setConsultFormats] = useState(profile.consultFormats);
  const [consultButtonText, setConsultButtonText] = useState(profile.consultButtonText);
  // Services — initialized from context
  const [services, setServices] = useState(profile.services);
  // Stats block
  const [showStats, setShowStats] = useState(true);
  const statsData = {
    consultations: 1250,
    rating: 94,
    responseTime: 15
  };
  // Reviews settings
  const [reviewsMode, setReviewsMode] = useState<'all' | 'best'>('best');
  const [pinnedReviews] = useState([1, 3]);
  // Products settings
  const [showProducts, setShowProducts] = useState(true);
  const [productsCount, setProductsCount] = useState<3 | 6 | 'all'>(3);
  // Profile blocks (constructor)
  const [blocks, setBlocks] = useState<ProfileBlock[]>([{
    id: 'about',
    label: 'Обо мне',
    enabled: true,
    location: 'Первый текстовый блок под шапкой'
  }, {
    id: 'services',
    label: 'Услуги',
    enabled: true,
    location: 'Блок «Услуги» на странице'
  }, {
    id: 'products',
    label: 'Товары / Курсы',
    enabled: false,
    location: 'Блок «Товары» после услуг'
  }, {
    id: 'reviews',
    label: 'Отзывы',
    enabled: true,
    location: 'Блок «Отзывы клиентов»'
  }, {
    id: 'education',
    label: 'Образование',
    enabled: true,
    location: 'Блок «Образование и сертификаты»'
  }, {
    id: 'faq',
    label: 'FAQ',
    enabled: true,
    location: 'Блок «Частые вопросы» внизу'
  }]);
  // Verification
  const [verificationStatus, setVerificationStatus] = useState<'unverified' | 'pending' | 'verified'>('unverified');
  // Collapsed sections
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    contacts: true,
    services: false,
    consultation: true,
    constructor: true,
    stats: true,
    reviews: true,
    products: true,
    verification: true
  });
  const toggle = (s: string) => setCollapsed(p => ({
    ...p,
    [s]: !p[s]
  }));
  // Progress
  const progress = useMemo(() => {
    let score = 0,
      total = 8;
    if (firstName.trim()) score++;
    if (lastName.trim()) score++;
    if (aboutText.trim().length > 50) score++;
    if (specializations.length >= 2) score++;
    if (contacts.some(c => c.enabled && c.value.trim())) score++;
    if (services.length > 0) score++;
    if (experience.trim()) score++;
    if (consultPrice.trim()) score++;
    return Math.round(score / total * 100);
  }, [firstName, lastName, aboutText, specializations, contacts, services, experience, consultPrice]);
  const filteredSpecs = ALL_SPECIALIZATIONS.filter(s => !specializations.includes(s) && s.toLowerCase().includes(specSearch.toLowerCase()));
  const moveBlock = (idx: number, dir: 'up' | 'down') => {
    setBlocks(prev => {
      const next = [...prev];
      const t = dir === 'up' ? idx - 1 : idx + 1;
      if (t < 0 || t >= next.length) return prev;
      [next[idx], next[t]] = [next[t], next[idx]];
      return next;
    });
  };
  const applyRecommendedOrder = () => {
    setBlocks(prev => {
      const sorted = [...prev].sort((a, b) => {
        const ai = RECOMMENDED_ORDER.indexOf(a.id);
        const bi = RECOMMENDED_ORDER.indexOf(b.id);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
      return sorted;
    });
    showToast('Рекомендуемый порядок применён');
  };
  const tips = useMemo(() => {
    const t: {
      text: string;
      action: string;
    }[] = [];
    if (aboutText.trim().length < 100) t.push({
      text: 'Добавьте описание (мин. 100 символов)',
      action: 'Написать'
    });
    if (specializations.length < 3) t.push({
      text: 'Добавьте больше специализаций',
      action: 'Добавить'
    });
    if (services.length < 3) t.push({
      text: 'Астрологи с 3+ услугами получают на 30% больше заказов',
      action: 'Добавить'
    });
    if (verificationStatus === 'unverified') t.push({
      text: 'Верификация повышает доверие клиентов',
      action: 'Пройти'
    });
    return t;
  }, [aboutText, specializations, services, verificationStatus]);
  const handleSave = () => {
    const errors: string[] = [];
    if (!firstName.trim() || !lastName.trim()) errors.push('Укажите имя и фамилию');
    if (aboutText.trim().length < 30) errors.push('Описание слишком короткое');
    if (services.length === 0) errors.push('Добавьте хотя бы 1 услугу');
    if (errors.length > 0) {
      showToast(`⚠️ ${errors[0]}`);
      return;
    }
    // Push all changes to shared context (persists to localStorage)
    updateProfile({
      firstName,
      lastName,
      experience,
      languages,
      aboutText,
      specializations,
      avatarUrl,
      services,
      contacts,
      consultPrice,
      consultPriceType,
      consultDuration,
      consultFormats,
      consultButtonText,
      showChatButton,
      showBookButton
    });
    showToast('Изменения сохранены и обновлены в каталоге ✓');
    console.log('[Admin Sync] Profile updated and persisted to context');
  };
  const addCustomSpec = () => {
    const val = customSpecInput.trim();
    if (val && !specializations.includes(val)) {
      setSpecializations(p => [...p, val]);
      setCustomSpecInput('');
      setShowCustomSpecInput(false);
      showToast('Специализация добавлена');
    }
  };
  // Section header helper
  const SectionHeader = ({
    title,
    section,
    badge
  }: {
    title: string;
    section: string;
    badge?: React.ReactNode;
  }) => <div className="flex justify-between items-center cursor-pointer" onClick={() => toggle(section)} data-id="element-14247">
      <h3 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-14248">{title}</h3>
      <div className="flex items-center gap-2" data-id="element-14249">
        {badge}
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${!collapsed[section] ? 'rotate-90' : ''}`} data-id="element-14250" />
      </div>
    </div>;
  const SectionBody = ({
    section,
    children
  }: {
    section: string;
    children: React.ReactNode;
  }) => <div className={`overflow-hidden transition-all duration-300 ${!collapsed[section] ? 'max-h-[5000px] mt-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-14251">
      {children}
    </div>;
  // Mini preview component
  const MiniPreview = () => <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm ${previewDevice === 'mobile' ? 'max-w-[320px] mx-auto' : 'w-full'}`} data-id="element-14252">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white" data-id="element-14253">
        <div className="flex items-center gap-3" data-id="element-14254">
          {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-14 h-14 rounded-full object-cover border-2 border-white/30" data-id="element-14255" /> : <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold" data-id="element-14256">
              {firstName.charAt(0)}
              {lastName.charAt(0)}
            </div>}
          <div data-id="element-14257">
            <div className="font-bold text-lg" data-id="element-14258">
              {firstName} {lastName}
            </div>
            <div className="text-indigo-100 text-xs" data-id="element-14259">
              {specializations.slice(0, 2).join(' · ')}
            </div>
            <div className="flex items-center gap-2 mt-1" data-id="element-14260">
              <span className="text-xs" data-id="element-14261">⭐ 4.9</span>
              <span className="text-xs text-indigo-200" data-id="element-14262">
                · {experience} лет опыта
              </span>
            </div>
          </div>
        </div>
      </div>

      {showStats && <div className={`grid grid-cols-3 gap-2 p-3 border-b border-slate-100 ${highlightedBlock === 'stats' ? 'ring-2 ring-indigo-400 bg-indigo-50' : ''}`} data-id="element-14263">
          <div className="text-center" data-id="element-14264">
            <div className="font-bold text-sm text-slate-900" data-id="element-14265">
              {statsData.consultations}+
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-14266">консультаций</div>
          </div>
          <div className="text-center" data-id="element-14267">
            <div className="font-bold text-sm text-slate-900" data-id="element-14268">
              {statsData.rating}%
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-14269">довольны</div>
          </div>
          <div className="text-center" data-id="element-14270">
            <div className="font-bold text-sm text-slate-900" data-id="element-14271">
              {statsData.responseTime} мин
            </div>
            <div className="text-[10px] text-slate-500" data-id="element-14272">ответ</div>
          </div>
        </div>}

      <div className="p-3 space-y-3" data-id="element-14273">
        {blocks.filter(b => b.enabled).map(block => <div key={block.id} className={`p-2 rounded-lg border text-xs transition-all ${highlightedBlock === block.id ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-400' : 'border-slate-100'}`} data-id="element-14274">
              <div className="font-bold text-slate-700 mb-0.5" data-id="element-14275">
                {block.label}
              </div>
              {block.id === 'about' && <div className="text-slate-500 line-clamp-2" data-id="element-14276">
                  {aboutText.slice(0, 80)}...
                </div>}
              {block.id === 'services' && <div className="text-slate-500" data-id="element-14277">
                  {services.length} услуг · от{' '}
                  {consultPriceType === 'from' ? '' : ''}
                  {consultPrice} ₽
                </div>}
              {block.id === 'reviews' && <div className="text-slate-500" data-id="element-14278">⭐ 4.9 · 124 отзыва</div>}
              {block.id === 'products' && <div className="text-slate-500" data-id="element-14279">Курсы и товары</div>}
              {block.id === 'education' && <div className="text-slate-500" data-id="element-14280">Образование и сертификаты</div>}
              {block.id === 'faq' && <div className="text-slate-500" data-id="element-14281">Частые вопросы</div>}
            </div>)}
      </div>

      <div className="p-3 border-t border-slate-100 space-y-2" data-id="element-14282">
        {showBookButton && <div className="w-full py-2 bg-indigo-600 text-white text-center rounded-lg text-xs font-bold" data-id="element-14283">
            {consultButtonText}
          </div>}
        {showChatButton && <div className="w-full py-2 bg-slate-100 text-slate-700 text-center rounded-lg text-xs font-medium" data-id="element-14284">
            Написать в чат
          </div>}
      </div>
    </div>;
  return <div className="space-y-5 pb-20 sm:pb-6" data-id="element-14285">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-14286">
        <div data-id="element-14287">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-14288">
            Редактирование профиля
          </h2>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-14289">
            Что редактируете → сразу видите на странице клиента
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto" data-id="element-14290">
          <Button variant={showPreview ? 'primary' : 'outline'} size="sm" icon={<Eye className="w-4 h-4" data-id="element-14292" />} onClick={() => setShowPreview(!showPreview)} className="flex-1 sm:flex-none" data-id="element-14291">
            {showPreview ? 'Скрыть превью' : 'Превью'}
          </Button>
          <Button variant="outline" size="sm" icon={<ExternalLink className="w-4 h-4" data-id="element-14294" />} onClick={() => onNavigate('astrologer-profile')} className="flex-1 sm:flex-none" data-id="element-14293">
            Открыть страницу
          </Button>
          <Button variant="primary" size="sm" icon={<Save className="w-4 h-4" data-id="element-14296" />} onClick={handleSave} className="flex-1 sm:flex-none" data-id="element-14295">
            Сохранить
          </Button>
        </div>
      </div>

      {/* Progress */}
      <Card padding="sm" className={progress === 100 ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'} data-id="element-14297">
        <div className="flex items-center justify-between mb-1.5" data-id="element-14298">
          <span className="text-sm font-bold text-slate-900" data-id="element-14299">
            Заполнение профиля
          </span>
          <span className={`text-sm font-bold ${progress === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14300">
            {progress}%
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-14301">
          <div className={`h-2 rounded-full transition-all duration-500 ${progress === 100 ? 'bg-emerald-500' : progress >= 70 ? 'bg-amber-400' : 'bg-rose-400'}`} style={{
          width: `${progress}%`
        }} data-id="element-14302" />
        </div>
        {progress < 100 && <p className="text-xs text-amber-700 mt-1.5" data-id="element-14303">
            Заполните полностью → до +40% заказов
          </p>}
      </Card>

      {/* Tips */}
      {tips.length > 0 && <Card padding="sm" data-id="element-14304">
          <div className="flex items-center gap-2 mb-2" data-id="element-14305">
            <Lightbulb className="w-4 h-4 text-amber-500" data-id="element-14306" />
            <span className="text-sm font-bold text-slate-900" data-id="element-14307">
              Как улучшить профиль
            </span>
          </div>
          <div className="space-y-1.5" data-id="element-14308">
            {tips.slice(0, 3).map((tip, i) => <div key={i} className="flex items-center justify-between gap-3 text-sm" data-id="element-14309">
                <span className="text-slate-600 flex-1" data-id="element-14310">{tip.text}</span>
                <button className="text-indigo-600 font-medium text-xs hover:text-indigo-700 shrink-0" data-id="element-14311">
                  {tip.action} →
                </button>
              </div>)}
          </div>
        </Card>}

      {/* Main layout: Editor + Preview */}
      <div className={`flex gap-6 ${showPreview ? 'flex-col lg:flex-row' : ''}`} data-id="element-14312">
        {/* Editor Column */}
        <div className={`space-y-5 ${showPreview ? 'flex-1 min-w-0' : 'w-full'}`} data-id="element-14313">
          {/* Main Info */}
          <Card padding="md" data-id="element-14314">
            <div className="flex justify-between items-center mb-4" data-id="element-14315">
              <h3 className="font-bold text-slate-900" data-id="element-14316">Основная информация</h3>
              <Badge variant="neutral" className="bg-slate-100 text-[10px]" data-id="element-14317">
                <Lock className="w-3 h-3 mr-1" data-id="element-14318" /> Обязательно
              </Badge>
            </div>
            <p className="text-[11px] text-indigo-600 mb-4 flex items-center gap-1" data-id="element-14319">
              <MapPin className="w-3 h-3" data-id="element-14320" /> Отображается: верх профиля (имя,
              рейтинг, опыт)
            </p>

            <div className="flex flex-col md:flex-row gap-5 mb-5" data-id="element-14321">
              <div className="flex flex-col items-center gap-2 w-28 shrink-0" data-id="element-14322">
                <div className="relative w-28 h-28 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden group" data-id="element-14323">
                  {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-full h-full object-cover" data-id="element-14324" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center" data-id="element-14325">
                      <span className="text-3xl font-bold text-indigo-300" data-id="element-14326">
                        {firstName.charAt(0)}
                        {lastName.charAt(0)}
                      </span>
                    </div>}
                  <button onClick={handleAvatarUpload} className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-id="element-14327">
                    <Camera className="w-5 h-5 text-white" data-id="element-14328" />
                  </button>
                </div>
                <button onClick={handleAvatarUpload} className="text-xs text-indigo-600 font-medium hover:underline" data-id="element-14329">
                  {avatarUrl ? 'Заменить' : 'Загрузить фото'}
                </button>
                {avatarUrl && <button onClick={() => {
                setAvatarUrl(null);
                showToast('Фото удалено');
              }} className="text-xs text-rose-500 font-medium hover:underline" data-id="element-14330">
                    Удалить
                  </button>}
              </div>
              <div className="flex-1 space-y-3" data-id="element-14331">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14332">
                  <div data-id="element-14333">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14334">
                      Имя
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14335" />
                  </div>
                  <div data-id="element-14336">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14337">
                      Фамилия
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14338" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-14339">
                  <div data-id="element-14340">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14341">
                      Опыт (лет)
                    </label>
                    <input type="number" value={experience} onChange={e => setExperience(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14342" />
                  </div>
                  <div data-id="element-14343">
                    <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14344">
                      Языки
                    </label>
                    <input type="text" value={languages} onChange={e => setLanguages(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm" data-id="element-14345" />
                  </div>
                </div>
              </div>
            </div>

            {/* Specializations */}
            <div className="mb-5" data-id="element-14346">
              <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-14347">
                Специализации
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-14348">
                {specializations.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium border border-indigo-100" data-id="element-14349">
                    {tag}{' '}
                    <X className="w-3 h-3 cursor-pointer hover:text-rose-500" onClick={() => setSpecializations(p => p.filter(s => s !== tag))} data-id="element-14350" />
                  </span>)}
              </div>
              <div className="flex flex-col sm:flex-row gap-2" data-id="element-14351">
                <div className="relative flex-1 min-w-0" data-id="element-14352">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-14353" />
                  <input type="text" value={specSearch} onChange={e => {
                  setSpecSearch(e.target.value);
                  setShowSpecDropdown(true);
                }} onFocus={() => setShowSpecDropdown(true)} placeholder="Поиск..." className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14354" />
                  {showSpecDropdown && filteredSpecs.length > 0 && <div className="absolute z-10 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-36 overflow-y-auto" data-id="element-14355">
                      {filteredSpecs.map(s => <button key={s} onClick={() => {
                    setSpecializations(p => [...p, s]);
                    setSpecSearch('');
                    setShowSpecDropdown(false);
                  }} className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700" data-id="element-14356">
                          {s}
                        </button>)}
                    </div>}
                </div>
                {showCustomSpecInput ? <div className="flex items-center gap-1" data-id="element-14357">
                    <input type="text" value={customSpecInput} onChange={e => setCustomSpecInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') addCustomSpec();
                  if (e.key === 'Escape') setShowCustomSpecInput(false);
                }} placeholder="Своя..." className="w-32 rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" autoFocus data-id="element-14358" />
                    <button onClick={addCustomSpec} className="p-1.5 text-emerald-500 hover:text-emerald-700" data-id="element-14359">
                      <Check className="w-4 h-4" data-id="element-14360" />
                    </button>
                    <button onClick={() => {
                  setShowCustomSpecInput(false);
                  setCustomSpecInput('');
                }} className="p-1.5 text-slate-400 hover:text-slate-600" data-id="element-14361">
                      <X className="w-4 h-4" data-id="element-14362" />
                    </button>
                  </div> : <Button variant="outline" size="sm" onClick={() => setShowCustomSpecInput(true)} icon={<Plus className="w-3.5 h-3.5" data-id="element-14364" />} data-id="element-14363">
                    Своя
                  </Button>}
              </div>
            </div>

            {/* About */}
            <div data-id="element-14365">
              <div className="flex justify-between items-center mb-1" data-id="element-14366">
                <label className="text-xs font-medium text-slate-600" data-id="element-14367">
                  О себе
                </label>
                <button onClick={() => setShowAboutHint(!showAboutHint)} className="text-[11px] text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-14368">
                  {showAboutHint ? 'Скрыть' : 'Что писать?'}
                </button>
              </div>
              <p className="text-[11px] text-indigo-600 mb-2 flex items-center gap-1" data-id="element-14369">
                <MapPin className="w-3 h-3" data-id="element-14370" /> Отображается: первый текстовый
                блок под шапкой
              </p>
              {showAboutHint && <div className="mb-2 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-700 space-y-1" data-id="element-14371">
                  <p data-id="element-14372">
                    Расскажите о себе: опыт, подход, чем отличаетесь. Клиенты
                    выбирают тех, кому доверяют.
                  </p>
                  <p className="text-indigo-500 italic" data-id="element-14373">
                    Пример: «Привет! Я Елена, астролог с 8-летним стажем. Мой
                    подход — перевести язык звёзд на понятный человеческий.»
                  </p>
                </div>}
              <textarea className="w-full rounded-lg border-slate-200 px-3 py-2.5 border focus:ring-2 focus:ring-indigo-500 outline-none text-sm resize-none" rows={3} value={aboutText} onChange={e => setAboutText(e.target.value.slice(0, MAX_ABOUT_LENGTH))} onFocus={() => setHighlightedBlock('about')} onBlur={() => setHighlightedBlock(null)} data-id="element-14374" />
              <div className="flex justify-end mt-0.5" data-id="element-14375">
                <span className={`text-[11px] ${aboutText.length > 900 ? 'text-rose-500' : 'text-slate-400'}`} data-id="element-14376">
                  {aboutText.length}/{MAX_ABOUT_LENGTH}
                </span>
              </div>
            </div>
          </Card>

          {/* Consultation Settings */}
          <Card padding="md" data-id="element-14377">
            <SectionHeader title="Запись на консультацию" section="consultation" badge={<Badge variant="success" size="sm" data-id="element-14379">
                  Ключевой блок
                </Badge>} data-id="element-14378" />
            <SectionBody section="consultation" data-id="element-14380">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14381">
                <MapPin className="w-3 h-3" data-id="element-14382" /> Отображается: боковая панель
                справа (кнопка записи)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3" data-id="element-14383">
                <div data-id="element-14384">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14385">
                    Цена
                  </label>
                  <div className="flex gap-2" data-id="element-14386">
                    <select value={consultPriceType} onChange={e => setConsultPriceType(e.target.value as 'fixed' | 'from')} className="rounded-lg border-slate-200 px-2 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-14387">
                      <option value="from" data-id="element-14388">от</option>
                      <option value="fixed" data-id="element-14389">фикс</option>
                    </select>
                    <input type="number" value={consultPrice} onChange={e => setConsultPrice(e.target.value)} className="flex-1 rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="5000" data-id="element-14390" />
                    <span className="flex items-center text-sm text-slate-500" data-id="element-14391">
                      ₽
                    </span>
                  </div>
                </div>
                <div data-id="element-14392">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14393">
                    Длительность (мин)
                  </label>
                  <input type="number" value={consultDuration} onChange={e => setConsultDuration(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-14394" />
                </div>
              </div>
              <div className="mb-3" data-id="element-14395">
                <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-14396">
                  Формат
                </label>
                <div className="flex gap-2" data-id="element-14397">
                  {[{
                  key: 'video' as const,
                  icon: Video,
                  label: 'Видео'
                }, {
                  key: 'chat' as const,
                  icon: MessageSquare,
                  label: 'Чат'
                }, {
                  key: 'audio' as const,
                  icon: Mic,
                  label: 'Аудио'
                }].map(f => <button key={f.key} onClick={() => setConsultFormats(p => ({
                  ...p,
                  [f.key]: !p[f.key]
                }))} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${consultFormats[f.key] ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`} data-id="element-14398">
                      <f.icon className="w-4 h-4" data-id="element-14399" /> {f.label}
                    </button>)}
                </div>
              </div>
              <div data-id="element-14400">
                <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-14401">
                  Текст кнопки
                </label>
                <input type="text" value={consultButtonText} onChange={e => setConsultButtonText(e.target.value)} className="w-full rounded-lg border-slate-200 px-3 py-2 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none" data-id="element-14402" />
              </div>
            </SectionBody>
          </Card>

          {/* Contacts */}
          <Card padding="md" data-id="element-14403">
            <SectionHeader title="Контакты и кнопки связи" section="contacts" data-id="element-14404" />
            <SectionBody section="contacts" data-id="element-14405">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14406">
                <MapPin className="w-3 h-3" data-id="element-14407" /> Отображается: блок «Связаться» на
                странице
              </p>
              <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-3" data-id="element-14408">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" data-id="element-14409" />
                <p className="text-xs text-amber-700" data-id="element-14410">
                  Не уводите клиентов с платформы. Заказы через AstroGuide —
                  безопасность для вас и клиентов.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-4" data-id="element-14411">
                <button onClick={() => setShowChatButton(!showChatButton)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium whitespace-nowrap ${showChatButton ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14412">
                  {showChatButton ? <ToggleRight className="w-5 h-5 text-emerald-500 flex-shrink-0" data-id="element-14413" /> : <ToggleLeft className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-14414" />}{' '}
                  Кнопка «Написать»
                </button>
                <button onClick={() => setShowBookButton(!showBookButton)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium whitespace-nowrap ${showBookButton ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14415">
                  {showBookButton ? <ToggleRight className="w-5 h-5 text-emerald-500 flex-shrink-0" data-id="element-14416" /> : <ToggleLeft className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-14417" />}{' '}
                  Кнопка «Записаться»
                </button>
              </div>
              <div className="space-y-2" data-id="element-14418">
                {contacts.map((c, idx) => <div key={c.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${c.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-50'}`} data-id="element-14419">
                    <button onClick={() => setContacts(prev => prev.map((ct, i) => i === idx ? {
                  ...ct,
                  enabled: !ct.enabled
                } : ct))} className="shrink-0" data-id="element-14420">
                      {c.enabled ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-14421" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-14422" />}
                    </button>
                    <span className={`w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold shrink-0 ${c.color}`} data-id="element-14423">
                      {c.code}
                    </span>
                    <input type="text" value={c.value} onChange={e => setContacts(prev => prev.map((ct, i) => i === idx ? {
                  ...ct,
                  value: e.target.value
                } : ct))} placeholder={c.label} disabled={!c.enabled} className="flex-1 rounded-lg border-slate-200 px-2.5 py-1.5 border text-sm focus:ring-2 focus:ring-indigo-500 outline-none disabled:bg-slate-50 disabled:text-slate-400 min-w-0" data-id="element-14424" />
                  </div>)}
              </div>
            </SectionBody>
          </Card>

          {/* Stats Block */}
          <Card padding="md" data-id="element-14425">
            <SectionHeader title="Блок статистики" section="stats" data-id="element-14426" />
            <SectionBody section="stats" data-id="element-14427">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14428">
                <MapPin className="w-3 h-3" data-id="element-14429" /> Отображается: под шапкой (1250+,
                94%, 15 мин)
              </p>
              <div className="flex items-center gap-3 mb-3" data-id="element-14430">
                <button onClick={() => setShowStats(!showStats)} className="shrink-0" data-id="element-14431">
                  {showStats ? <ToggleRight className="w-6 h-6 text-emerald-500" data-id="element-14432" /> : <ToggleLeft className="w-6 h-6 text-slate-300" data-id="element-14433" />}
                </button>
                <span className="text-sm font-medium text-slate-700" data-id="element-14434">
                  {showStats ? 'Блок включён' : 'Блок выключен'}
                </span>
              </div>
              {showStats && <div className="grid grid-cols-3 gap-3" data-id="element-14435">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14436">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14437">
                      {statsData.consultations}+
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14438">
                      Консультаций (авто)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14439">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14440">
                      {statsData.rating}%
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14441">
                      Довольны (авто)
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center" data-id="element-14442">
                    <div className="text-lg font-bold text-slate-900" data-id="element-14443">
                      {statsData.responseTime} мин
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-14444">
                      Время ответа (авто)
                    </div>
                  </div>
                </div>}
            </SectionBody>
          </Card>

          {/* Services */}
          <Card padding="md" data-id="element-14445">
            <SectionHeader title="Услуги" section="services" data-id="element-14446" />
            <SectionBody section="services" data-id="element-14447">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14448">
                <MapPin className="w-3 h-3" data-id="element-14449" /> Отображается: блок «Услуги» на
                странице
              </p>
              <div className="space-y-2 mb-3" data-id="element-14450">
                {services.map((srv, idx) => <div key={srv.id} className={`flex items-center gap-2.5 p-3 rounded-lg border ${srv.isTop ? 'border-amber-300 bg-amber-50/30' : 'border-slate-100 bg-slate-50/50'}`} onMouseEnter={() => setHighlightedBlock('services')} onMouseLeave={() => setHighlightedBlock(null)} data-id="element-14451">
                    <div className="flex flex-col gap-0.5 shrink-0" data-id="element-14452">
                      <button onClick={() => {
                    const n = [...services];
                    if (idx > 0) {
                      ;
                      [n[idx], n[idx - 1]] = [n[idx - 1], n[idx]];
                      setServices(n);
                    }
                  }} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14453">
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-14454" />
                      </button>
                      <button onClick={() => {
                    const n = [...services];
                    if (idx < n.length - 1) {
                      ;
                      [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]];
                      setServices(n);
                    }
                  }} disabled={idx === services.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14455">
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-14456" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14457">
                      <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5" data-id="element-14458">
                        {srv.isTop && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" data-id="element-14459" />}
                        {srv.name}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-14460">
                        {srv.price} · {srv.duration}
                      </div>
                    </div>
                    <button onClick={() => setServices(p => p.map(s => s.id === srv.id ? {
                  ...s,
                  isTop: !s.isTop
                } : s))} className={`p-1.5 rounded-lg transition-colors ${srv.isTop ? 'text-amber-500 bg-amber-100' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'}`} title="Выделить как топ" data-id="element-14461">
                      <Star className="w-4 h-4" data-id="element-14462" />
                    </button>
                    <Button variant="ghost" size="sm" className="text-indigo-600 shrink-0" onClick={() => {
                  if (setActiveTab) setActiveTab('services');else showToast(`Редактирование: ${srv.name}`);
                }} data-id="element-14463">
                      Изменить
                    </Button>
                  </div>)}
              </div>
              <Button variant="primary" size="sm" icon={<Plus className="w-3.5 h-3.5" data-id="element-14465" />} onClick={() => {
              if (setActiveTab) setActiveTab('services');else showToast('Создание услуги');
            }} data-id="element-14464">
                Добавить услугу
              </Button>
            </SectionBody>
          </Card>

          {/* Reviews Settings */}
          <Card padding="md" data-id="element-14466">
            <SectionHeader title="Отзывы" section="reviews" data-id="element-14467" />
            <SectionBody section="reviews" data-id="element-14468">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14469">
                <MapPin className="w-3 h-3" data-id="element-14470" /> Отображается: блок «Отзывы
                клиентов»
              </p>
              <div className="flex gap-2 mb-3" data-id="element-14471">
                {(['all', 'best'] as const).map(m => <button key={m} onClick={() => setReviewsMode(m)} className={`px-3 py-2 rounded-lg border text-sm font-medium ${reviewsMode === m ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14472">
                    {m === 'all' ? 'Все отзывы' : 'Лучшие (4-5 ⭐)'}
                  </button>)}
              </div>
              <p className="text-xs text-slate-500" data-id="element-14473">
                Закреплённых отзывов: {pinnedReviews.length}. Управление
                отзывами → вкладка «Отзывы».
              </p>
            </SectionBody>
          </Card>

          {/* Products Settings */}
          <Card padding="md" data-id="element-14474">
            <SectionHeader title="Товары / Курсы" section="products" data-id="element-14475" />
            <SectionBody section="products" data-id="element-14476">
              <p className="text-[11px] text-indigo-600 mb-3 flex items-center gap-1" data-id="element-14477">
                <MapPin className="w-3 h-3" data-id="element-14478" /> Отображается: блок «Товары» после
                услуг
              </p>
              <div className="flex items-center gap-3 mb-3" data-id="element-14479">
                <button onClick={() => setShowProducts(!showProducts)} data-id="element-14480">
                  {showProducts ? <ToggleRight className="w-6 h-6 text-emerald-500" data-id="element-14481" /> : <ToggleLeft className="w-6 h-6 text-slate-300" data-id="element-14482" />}
                </button>
                <span className="text-sm font-medium text-slate-700" data-id="element-14483">
                  {showProducts ? 'Блок включён' : 'Блок выключен'}
                </span>
              </div>
              {showProducts && <div data-id="element-14484">
                  <label className="block text-xs font-medium text-slate-600 mb-2" data-id="element-14485">
                    Показывать
                  </label>
                  <div className="flex gap-2" data-id="element-14486">
                    {([3, 6, 'all'] as const).map(n => <button key={String(n)} onClick={() => setProductsCount(n)} className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${productsCount === n ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500'}`} data-id="element-14487">
                        {n === 'all' ? 'Все' : `${n} шт`}
                      </button>)}
                  </div>
                </div>}
            </SectionBody>
          </Card>

          {/* Profile Constructor */}
          <Card padding="md" data-id="element-14488">
            <SectionHeader title="Конструктор профиля" section="constructor" data-id="element-14489" />
            <SectionBody section="constructor" data-id="element-14490">
              <div className="flex justify-between items-center mb-3" data-id="element-14491">
                <p className="text-xs text-slate-500" data-id="element-14492">
                  Порядок и видимость блоков на публичной странице
                </p>
                <Button variant="ghost" size="sm" className="text-indigo-600" icon={<Sparkles className="w-3.5 h-3.5" data-id="element-14494" />} onClick={applyRecommendedOrder} data-id="element-14493">
                  Рекомендуемый
                </Button>
              </div>
              <div className="space-y-1.5" data-id="element-14495">
                {blocks.map((block, idx) => <div key={block.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-colors ${block.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-50'}`} onMouseEnter={() => setHighlightedBlock(block.id)} onMouseLeave={() => setHighlightedBlock(null)} data-id="element-14496">
                    <div className="flex flex-col gap-0.5 shrink-0" data-id="element-14497">
                      <button onClick={() => moveBlock(idx, 'up')} disabled={idx === 0} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14498">
                        <ChevronUp className="w-3.5 h-3.5" data-id="element-14499" />
                      </button>
                      <button onClick={() => moveBlock(idx, 'down')} disabled={idx === blocks.length - 1} className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30" data-id="element-14500">
                        <ChevronDown className="w-3.5 h-3.5" data-id="element-14501" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-14502">
                      <span className="text-sm font-medium text-slate-900" data-id="element-14503">
                        {block.label}
                      </span>
                      <div className="text-[10px] text-slate-400" data-id="element-14504">
                        {block.location}
                      </div>
                    </div>
                    <button onClick={() => {
                  setHighlightedBlock(block.id);
                  setTimeout(() => setHighlightedBlock(null), 2000);
                }} className="p-1 text-slate-400 hover:text-indigo-600" title="Показать на превью" data-id="element-14505">
                      <Eye className="w-3.5 h-3.5" data-id="element-14506" />
                    </button>
                    <button onClick={() => setBlocks(prev => prev.map(b => b.id === block.id ? {
                  ...b,
                  enabled: !b.enabled
                } : b))} data-id="element-14507">
                      {block.enabled ? <ToggleRight className="w-5 h-5 text-emerald-500" data-id="element-14508" /> : <ToggleLeft className="w-5 h-5 text-slate-300" data-id="element-14509" />}
                    </button>
                  </div>)}
              </div>
            </SectionBody>
          </Card>

          {/* Verification */}
          <Card padding="md" className="border-indigo-100 bg-indigo-50/30" data-id="element-14510">
            <SectionHeader title="Верификация" section="verification" badge={verificationStatus === 'unverified' ? <Badge variant="warning" size="sm" data-id="element-14512">
                    Не пройдена
                  </Badge> : verificationStatus === 'pending' ? <Badge variant="pro" size="sm" data-id="element-14513">
                    На проверке
                  </Badge> : <Badge variant="success" size="sm" data-id="element-14514">
                    Пройдена
                  </Badge>} data-id="element-14511" />
            <SectionBody section="verification" data-id="element-14515">
              <p className="text-xs text-slate-600 mb-3" data-id="element-14516">
                Верифицированные профили получают значок PRO и больше доверия.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-id="element-14517">
                {[{
                icon: FileCheck,
                title: 'Паспорт / ID'
              }, {
                icon: GraduationCap,
                title: 'Диплом'
              }, {
                icon: FileText,
                title: 'Доп. документы'
              }].map(doc => <div key={doc.title} className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:bg-white hover:border-indigo-300 cursor-pointer transition-colors" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*,.pdf';
                input.onchange = e => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    showToast(`${doc.title}: "${file.name}" загружен ✓`);
                  }
                };
                input.click();
              }} data-id="element-14518">
                    <doc.icon className="w-5 h-5 text-slate-400 mx-auto mb-1" data-id="element-14519" />
                    <div className="text-xs font-bold text-slate-900" data-id="element-14520">
                      {doc.title}
                    </div>
                    <span className="text-[10px] text-indigo-600 font-medium" data-id="element-14521">
                      Загрузить
                    </span>
                  </div>)}
              </div>
              <div className="flex justify-end" data-id="element-14522">
                <Button variant="primary" size="sm" disabled={verificationStatus !== 'unverified'} onClick={() => {
                setVerificationStatus('pending');
                showToast('Документы отправлены ✓');
              }} data-id="element-14523">
                  {verificationStatus === 'unverified' ? 'Отправить на проверку' : verificationStatus === 'pending' ? 'На проверке...' : 'Верифицирован ✓'}
                </Button>
              </div>
            </SectionBody>
          </Card>
        </div>

        {/* Preview Column */}
        {showPreview && <div className="lg:w-[360px] shrink-0 lg:sticky lg:top-4 lg:self-start" data-id="element-14524">
            <div className="flex items-center justify-between mb-3" data-id="element-14525">
              <span className="text-sm font-bold text-slate-900" data-id="element-14526">
                Предпросмотр
              </span>
              <div className="flex bg-slate-100 p-0.5 rounded-lg" data-id="element-14527">
                <button onClick={() => setPreviewDevice('desktop')} className={`p-1.5 rounded-md ${previewDevice === 'desktop' ? 'bg-white shadow-sm' : ''}`} data-id="element-14528">
                  <Monitor className="w-4 h-4 text-slate-600" data-id="element-14529" />
                </button>
                <button onClick={() => setPreviewDevice('mobile')} className={`p-1.5 rounded-md ${previewDevice === 'mobile' ? 'bg-white shadow-sm' : ''}`} data-id="element-14530">
                  <Smartphone className="w-4 h-4 text-slate-600" data-id="element-14531" />
                </button>
              </div>
            </div>
            <MiniPreview data-id="element-14532" />
          </div>}
      </div>

      {/* Mobile Sticky Save */}
      <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t border-slate-200 sm:hidden z-40 shadow-lg" data-id="element-14533">
        <Button variant="primary" fullWidth icon={<Save className="w-4 h-4" data-id="element-14535" />} onClick={handleSave} data-id="element-14534">
          Сохранить
        </Button>
      </div>
    </div>;
}